<template>
  <Pane :value="paneIsOpen"
        :tabs="tabs"
        current-tab="filter"
        location="left"
        temporary
        @input="onInput"
        @update:modelValue="closePane"
  >
    <template #content-filter>
      <div class="text-body-1 font-weight-bold mt-6">
        {{ $t('subscriptions.SubscriptionActiveProjectsFiltersPane.projectType') }}
      </div>

      <v-row no-gutters>
        <v-col v-for="(projectType, index) in projectTypes"
               :key="`projectType-${index}`"
               cols="12"
               md="6"
        >
          <v-checkbox v-model="localFilters.projectTypes"
                      :label="$t(`common.projectTypes.${projectType}`)"
                      :value="projectType"
                      hide-details
                      class="my-1 py-0"
          />
        </v-col>
      </v-row>

      <template v-if="roomLabels.length">
        <v-divider class="mt-4 mb-6" />

        <div class="text-body-1 font-weight-bold">
          {{ $t('subscriptions.SubscriptionActiveProjectsFiltersPane.labels') }}
        </div>
        <v-row no-gutters>
          <v-col v-for="(label) in roomLabels"
                 :key="`label-${label.id}`"
                 cols="12"
                 md="6"
          >
            <v-checkbox v-model="localFilters.labels"
                        :label="label.label[$i18n.locale]"
                        :ripple="false"
                        :value="label"
                        hide-details
                        class="my-1 py-0"
            />
          </v-col>
        </v-row>
      </template>

      <v-divider class="mt-4 mb-6" />

      <div class="text-body-1 font-weight-bold mb-4">
        {{ $t('subscriptions.SubscriptionActiveProjectsFiltersPane.createdAt') }}
      </div>
      <AppDateRangePicker v-model="localFilters.createdAt" />

      <template v-if="members.length > 0">
        <v-divider class="my-6" />

        <div class="text-body-1 font-weight-bold mb-4">
          {{ $t('subscriptions.SubscriptionActiveProjectsFiltersPane.members') }}
        </div>
        <v-row>
          <v-col v-for="(member, index) in members.length > 10 && !displayMoreMembers ? shortMembers : members"
                 :key="`member-${index}`"
                 cols="12"
                 lg="6"
          >
            <v-checkbox v-model="localFilters.members"
                        :label="member"
                        density="compact"
                        hide-details
                        :value="member"
            />
          </v-col>
        </v-row>
        <v-row v-if="members.length > 10 || displayMoreMembers">
          <v-col>
            <h4 class="displayMoreMembers clickable"
                 @click="displayMoreMembers = true"
            >
              {{ $t('subscriptions.SubscriptionActiveProjectsFiltersPane.displayMoreMembers') }}
            </h4>
          </v-col>
        </v-row>
        <v-row v-if="members.length > 10 && displayMoreMembers">
          <v-col>
            <h4 class="displayLessMembers clickable"
                @click="displayMoreMembers = false"
            >
              {{ $t('subscriptions.SubscriptionActiveProjectsFiltersPane.displayLessMembers') }}
            </h4>
          </v-col>
        </v-row>
      </template>

      <v-divider class="my-6" />

      <v-row>
        <v-col>
          <AppButton block variant="tonal" @click="onClearAllFilters">
            {{ $t('common.clear') }}
          </AppButton>
        </v-col>
        <v-col>
          <AppButton color="primary" block @click="onValidateClick">
            {{ $t('common.apply') }}
          </AppButton>
        </v-col>
      </v-row>
    </template>
  </Pane>
</template>

<script>
import { mapMutations, mapState } from 'vuex'

import AppDateRangePicker from '@/common/AppDateRangePicker.vue'
import AppButton from '@/common/buttons/AppButton.vue'
import Pane from '@/common/Pane.vue'
import { ISOToShortenedDate } from '@/common/utils/dates'
import { Filter } from '@/models/filter.model'
import { SET_ACTIVE_ROOMS_FILTERS, SET_ACTIVE_ROOMS_FILTERS_PANE_IS_OPEN } from '@/store/modules/subscription/mutation_types'

const INITIAL_FILTERS = {
  projectTypes: [],
  createdAt: [],
  labels: [],
  members: [],
}

export default {
  name: 'SubscriptionActiveProjectsFiltersPane',
  components: {
    AppButton,
    AppDateRangePicker,
    Pane,
  },
  data () {
    return {
      tabs: [{ id: 'filter', title: this.$t('subscriptions.SubscriptionActiveProjectsFiltersPane.filters') }],
      projectTypes: ['project', 'dataroom'],
      paneIsOpen: false,
      displayMoreMembers: false,
      localFilters: { ...INITIAL_FILTERS },
    }
  },
  computed: {
    ...mapState('subscription', ['activeRoomsFiltersPaneIsOpen', 'activeRoomsFilters', 'activeRooms']),
    members () {
      const members = []
      if (this.activeRooms?.data) {
        this.activeRooms.data.forEach(room => {
          if (room.usersCount !== -1) {
            room.users.forEach((user) => {
              if (!members.includes(user.fullName)) {
                members.push(user.fullName)
              }
            })
          }
        })
      }
      return members
    },
    shortMembers () {
      return this.members.slice(0, 10)
    },
    roomLabels () {
      const labels = []
      if (this.activeRooms?.data) {
        this.activeRooms.data.forEach(room => {
          if (room.label && !labels.find(lbl => lbl.id === room.roomLabel.id)) {
            labels.push(room.roomLabel)
          }
        })
      }
      return labels
    },
  },
  watch: {
    activeRoomsFiltersPaneIsOpen: {
      handler (value) {
        if (value) {
          this.syncAppliedFiltersWithLocals()
          setTimeout(() => { this.paneIsOpen = true }, 300)
        }
      },
      immediate: true,
    },
    paneIsOpen (value) {
      if (!value) {
        setTimeout(() => {
          this.SET_ACTIVE_ROOMS_FILTERS_PANE_IS_OPEN(false)
        }, 300)
      }
    },
  },
  methods: {
    ...mapMutations('subscription', [
      SET_ACTIVE_ROOMS_FILTERS_PANE_IS_OPEN,
      SET_ACTIVE_ROOMS_FILTERS,
    ]),
    applyFilters () {
      const dateFilter = this.localFilters.createdAt.length > 0
        ? [
          new Filter(
            'createdAt',
            this.$t('subscriptions.SubscriptionActiveProjectsFiltersPane.createdAtFilter'),
            this.localFilters.createdAt,
            `${ISOToShortenedDate(this.localFilters.createdAt[0])} - ${ISOToShortenedDate(this.localFilters.createdAt[1])}`,
          )]
        : []

      const labelFilters = this.localFilters.labels.map(label =>
        new Filter('labels', this.$t('subscriptions.SubscriptionActiveProjectsFiltersPane.label'), label, label.label[this.$i18n.locale]),
      )

      const membersFilters = this.localFilters.members.map(member =>
        new Filter('members', this.$t('common.user'), member, member),
      )

      const projectTypeFilters = this.localFilters.projectTypes.map((type) =>
        new Filter(
          'projectType',
          this.$t('subscriptions.SubscriptionActiveProjectsFiltersPane.projectTypeFilter'),
          type,
          this.$t('common.projectTypes.' + type),
        ),
      )
      const filters = [
        ...dateFilter,
        ...membersFilters,
        ...labelFilters,
        ...projectTypeFilters,
      ]

      this.SET_ACTIVE_ROOMS_FILTERS(filters)
    },
    syncAppliedFiltersWithLocals () {
      const dateFilter = this.activeRoomsFilters.find(filter => filter.category.key === 'createdAt')
      this.localFilters.createdAt = dateFilter?.value.key ?? []

      const projectTypeFilters = this.activeRoomsFilters.filter((filter) => filter.category.key === 'projectType')
      this.localFilters.projectTypes = projectTypeFilters.map((filter) => filter.value.key)

      this.localFilters.members = this.activeRoomsFilters
        .filter(filter => filter.category.key === 'members')
        .map(filter => filter.value.key)

      this.localFilters.labels = this.activeRoomsFilters
        .filter(filter => filter.category.key === 'labels')
        .map(filter => filter.value.key)
    },
    onClearAllFilters () {
      this.SET_ACTIVE_ROOMS_FILTERS([])
    },
    onValidateClick () {
      this.applyFilters()
      this.closePane()
    },
    onInput (value) {
      if (!value) {
        this.closePane()
      }
    },
    closePane () {
      this.paneIsOpen = false
    },
  },
}
</script>
